/**
 * Qué órdenes de soporte entiende un equipo, según lo que lleva instalado.
 *
 * Ofrecer una que no entiende no da error en ninguna parte: el equipo la
 * descarta en silencio y quien la mandó se queda esperando una respuesta que no
 * llega, y lo lee como "el equipo no responde". Y en un caso es peor que eso:
 * un EFR32 anterior a 4.6.7 ignora el campo que dice hacia dónde va la orden de
 * emparejamiento, así que "cerrar" ABRE una ventana BLE de 120 s en el equipo
 * de un cliente.
 *
 * Reiniciar, borrar, reenviar listas, actualizar y abrir emparejamiento llevan
 * años en el firmware. Las demás se condicionan a la versión que las introdujo.
 */

export const SUPPORT_ORDERS = {
    STATUS: 'status',
    LINK: 'link',
    PAIRING_OPEN: 'pairing_open',
    PAIRING_CLOSE: 'pairing_close',
    RESTART: 'restart',
    FACTORY_RESET: 'factory_reset',
    RESYNC: 'resync',
    LOG_CONFIG: 'log_config',
    LOG_FETCH: 'log_fetch',
    UPDATE: 'update',
} as const;

/** Desde aquí el equipo entiende pedir estado y forzar el enlace (BAC-611). */
const MIN_VERSION_SUPPORT_ORDERS = '2026.08.022';
/** Y desde aquí, los logs del MiniHUB: pedirlos y configurar el nivel (BAC-558). */
const MIN_VERSION_LOGS = '2026.08.016';
/** Y desde aquí el EFR32 sabe SALIR del emparejamiento, no sólo entrar. */
const MIN_VERSION_BLE_CLOSE_PAIRING = '4.6.7';

/**
 * ¿`version` es al menos `minima`? Campo a campo y numéricamente, nunca como
 * texto: "2026.08.9" es POSTERIOR a "2026.08.10" comparando cadenas, y de ahí
 * salen respuestas al revés.
 *
 * Formas distintas (4.6.7 frente a 2026.08.022) o cualquier cosa que no sea
 * número devuelven false: sin poder compararlas, no se ofrece lo que no todos
 * entienden.
 */
function atLeast(version, minima) {
    if (!version || typeof version !== 'string') return false;
    const actual = version.split('.').map(Number);
    const tope = minima.split('.').map(Number);
    if (actual.length !== tope.length) return false;
    if (actual.some(isNaN)) return false;
    for (let i = 0; i < tope.length; i++) {
        if (actual[i] !== tope[i]) return actual[i] > tope[i];
    }
    return true;
}

export function supportsSupportOrder(order, device) {
    const version = device?.version;
    const versionBle = device?.versionBle;

    switch (order) {
        case SUPPORT_ORDERS.STATUS:
        case SUPPORT_ORDERS.LINK:
            return atLeast(version, MIN_VERSION_SUPPORT_ORDERS);

        case SUPPORT_ORDERS.LOG_CONFIG:
        case SUPPORT_ORDERS.LOG_FETCH:
            // Antes de esto el equipo no captura nada que mandar: pedirle el
            // registro lo deja mudo y quien lo pidió espera algo que no llega.
            return atLeast(version, MIN_VERSION_LOGS);

        case SUPPORT_ORDERS.PAIRING_CLOSE:
            // Las dos mitades: el ESP32 tiene que leer el campo y el EFR32
            // tiene que saber qué hacer con él.
            return atLeast(version, MIN_VERSION_SUPPORT_ORDERS)
                && atLeast(versionBle, MIN_VERSION_BLE_CLOSE_PAIRING);

        default:
            // Lo que el firmware entiende desde siempre.
            return true;
    }
}
